import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  LoaderCircle,
  MoonStar,
  ReceiptText,
  RotateCcw,
  Save,
  UsersRound,
} from "lucide-react";
import { calculateDaily, minutesLabel, validateDailyIntegrity, yen } from "../../shared/calculations";
import type { AppConfig, DailyPayload, ExpenseLine, SaveState, ShiftLine } from "../../shared/domain";
import { dailyPayloadSchema } from "../../shared/schema";
import { api } from "../api/client";
import { AddButton, ErrorSummary, LoadingBlock, MoneyInput, RowActions, Section } from "../components/ui";
import { addDays, emptyDaily, payloadFromRecord, todayInJapan, toLocalDateTime, toOffsetIso } from "../domain/form";

interface RecordMeta {
  revision: number;
  updatedAt: string;
  updatedBy: string;
  checksum: string;
}

const methodLabels: Record<string, string> = {
  CASH: "現金",
  PAYPAY: "PayPay",
  CARD: "カード",
  BANK: "振込",
  OTHER: "その他",
};

const saveStateLabels: Record<SaveState, string> = {
  idle: "未保存",
  dirty: "未保存の変更あり",
  saving: "保存中",
  saved: "保存済み",
  conflict: "更新競合",
  error: "保存失敗",
};

function emptyNoGuest(businessDate: string, config: AppConfig): DailyPayload {
  return {
    ...emptyDaily(businessDate, config),
    customers: [],
    note: "ノーゲス",
  };
}

function newExpense(): ExpenseLine {
  return { lineId: crypto.randomUUID(), vendorName: "", amount: 0, paymentMethod: "CASH", memo: "" };
}

function newShift(date: string, config: AppConfig): ShiftLine {
  const staff = config.staff.find((item) => item.active) ?? config.staff[0];
  return {
    lineId: crypto.randomUUID(),
    staffId: staff?.staffId ?? "",
    staffName: staff?.staffName ?? "",
    startedAt: `${date}T21:00:00+09:00`,
    endedAt: `${addDays(date, 1)}T02:00:00+09:00`,
    breakMinutes: 0,
    baseHourlyRateSnapshot: staff?.hourlyRate,
    paidTodayMethod: "UNPAID",
  };
}

const containsSalesData = (payload: DailyPayload) =>
  payload.customers.length > 0 ||
  payload.receivables.length > 0 ||
  payload.collections.length > 0 ||
  Object.values(payload.payments).some((value) => value !== 0);

export function NoGuestPage({ config, onDirtyChange }: { config: AppConfig; onDirtyChange: (dirty: boolean) => void }) {
  const initialDate = useMemo(() => new URLSearchParams(location.search).get("date") ?? todayInJapan(), []);
  const [businessDate, setBusinessDate] = useState(initialDate);
  const [payload, setPayload] = useState<DailyPayload>(() => emptyNoGuest(initialDate, config));
  const [meta, setMeta] = useState<RecordMeta | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [normalDayExists, setNormalDayExists] = useState(false);

  const dirty = saveState === "dirty" || saveState === "error" || saveState === "conflict";

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const loadDate = (date: string) => {
    setLoading(true);
    setMessage("");
    setNormalDayExists(false);
    api.getDaily(date)
      .then((response) => {
        if (!response.exists) {
          setPayload(emptyNoGuest(date, config));
          setMeta(null);
          setSaveState("idle");
          return;
        }
        const loaded = payloadFromRecord(response);
        if (containsSalesData(loaded)) {
          setNormalDayExists(true);
          setPayload(loaded);
          setMeta(null);
          setSaveState("idle");
          return;
        }
        setPayload({ ...loaded, note: loaded.note || "ノーゲス" });
        setMeta({
          revision: response.revision,
          updatedAt: response.updatedAt,
          updatedBy: response.updatedBy,
          checksum: response.checksum,
        });
        setSaveState("saved");
      })
      .catch((reason: Error) => {
        setMessage(reason.message);
        setSaveState("error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => loadDate(businessDate), [businessDate]);

  const changeDate = (date: string) => {
    if (!date || date === businessDate) return;
    if (dirty && !window.confirm("未保存の変更があります。営業日を変更しますか？")) return;
    setBusinessDate(date);
    history.replaceState({}, "", `/no-guest?date=${date}`);
  };

  const change = (producer: (current: DailyPayload) => DailyPayload) => {
    setPayload((current) => {
      const next = producer(structuredClone(current));
      if (saveState === "saved") next.requestId = crypto.randomUUID();
      return next;
    });
    setSaveState("dirty");
    setMessage("");
  };

  const updateExpense = (index: number, value: ExpenseLine) => change((draft) => {
    draft.expenses[index] = value;
    return draft;
  });
  const updateShift = (index: number, value: ShiftLine) => change((draft) => {
    draft.shifts[index] = value;
    return draft;
  });
  const removeExpense = (index: number) => change((draft) => {
    draft.expenses = draft.expenses.filter((_, itemIndex) => itemIndex !== index);
    return draft;
  });
  const removeShift = (index: number) => change((draft) => {
    draft.shifts = draft.shifts.filter((_, itemIndex) => itemIndex !== index);
    return draft;
  });

  const calculationResult = useMemo(() => validateDailyIntegrity(payload, config), [payload, config]);
  const calculation = calculationResult.calculation ?? calculateDaily({ ...payload, shifts: [] }, config);
  const schemaResult = useMemo(() => dailyPayloadSchema.safeParse(payload), [payload]);
  const validationMessages = useMemo(() => {
    const messages = [
      ...(schemaResult.success ? [] : schemaResult.error.issues.map((issue) => issue.message)),
      ...calculationResult.issues.map((issue) => issue.message),
    ];
    if (payload.expenses.length === 0 && payload.shifts.length === 0) {
      messages.push("経費または勤務を1件以上入力してください");
    }
    return [...new Set(messages)];
  }, [schemaResult, calculationResult.issues, payload.expenses.length, payload.shifts.length]);

  const save = async () => {
    if (validationMessages.length > 0 || normalDayExists) {
      setMessage("入力内容を確認してください");
      return;
    }
    if (meta && !window.confirm(`revision ${meta.revision} のノーゲス記録を更新します。よろしいですか？`)) return;
    setSaveState("saving");
    setMessage("");
    try {
      const savePayload: DailyPayload = {
        ...payload,
        customers: [],
        payments: { paypay: 0, card: 0, tab: 0, transferDue: 0 },
        receivables: [],
        collections: [],
        note: payload.note || "ノーゲス",
      };
      const response = await api.saveDaily(savePayload);
      if (response.revision !== response.data.revision || response.checksum !== response.data.checksum) {
        throw new Error("保存後の再読込検証に失敗しました。保存済みとして扱いません");
      }
      setPayload(payloadFromRecord(response.data));
      setMeta({
        revision: response.revision,
        updatedAt: response.savedAt,
        updatedBy: response.savedBy,
        checksum: response.checksum,
      });
      setSaveState("saved");
      setMessage(`ノーゲス日を revision ${response.revision} として保存しました`);
    } catch (reason) {
      const error = reason as Error & { code?: string };
      setSaveState(error.code === "REVISION_CONFLICT" ? "conflict" : "error");
      setMessage(error.message || "保存に失敗しました。入力内容は画面に残っています");
    }
  };

  if (loading) return <LoadingBlock label={`${businessDate} の記録を読み込んでいます`} />;

  return (
    <div className="page-stack no-guest-page">
      <header className="daily-hero">
        <div>
          <span className="eyebrow"><MoonStar size={15} /> ノーゲス記録</span>
          <h1>勤務・経費のみ入力</h1>
          <p>売上0円の営業日として、勤務時間と経費だけを記録します。</p>
        </div>
        <div className="daily-hero__controls">
          <label className="date-picker">
            <span>営業日</span>
            <input type="date" value={businessDate} onChange={(event) => changeDate(event.target.value)} />
          </label>
          <div className={`save-status save-status--${saveState}`}>
            {saveState === "saving" ? <LoaderCircle className="spin" size={16} /> : saveState === "saved" ? <CheckCircle2 size={16} /> : <span className="save-status__dot" />}
            <span>{saveStateLabels[saveState]}</span>
          </div>
        </div>
      </header>

      {normalDayExists ? (
        <div className="fatal-panel" role="alert">
          <h1>この営業日には売上データがあります</h1>
          <p>ノーゲス画面から上書きせず、通常の日次入力画面で編集してください。</p>
          <button type="button" className="button button--primary" onClick={() => { location.href = `/?date=${businessDate}`; }}>
            通常の日次入力を開く
          </button>
        </div>
      ) : (
        <>
          {meta ? (
            <div className="revision-strip">
              <span><strong>revision {meta.revision}</strong></span>
              <span>最終更新 {new Date(meta.updatedAt).toLocaleString("ja-JP")}</span>
              <span>{meta.updatedBy}</span>
              <span className="checksum" title={meta.checksum}>checksum {meta.checksum.slice(0, 10)}…</span>
            </div>
          ) : (
            <div className="revision-strip revision-strip--new"><strong>新規ノーゲス日</strong><span>売上0円で保存します</span></div>
          )}

          {message ? <div className={`inline-alert ${saveState === "saved" ? "inline-alert--success" : "inline-alert--error"}`} role="status">{message}</div> : null}

          <Section
            title="経費明細"
            description="ノーゲス日に発生した仕入れ・交通費などを入力します。"
            icon={<ReceiptText size={19} />}
            action={<AddButton onClick={() => change((draft) => ({ ...draft, expenses: [...draft.expenses, newExpense()] }))}>経費を追加</AddButton>}
          >
            {payload.expenses.length === 0 ? <p className="empty-inline">経費がある場合は追加してください。</p> : (
              <div className="line-list">
                {payload.expenses.map((row, index) => (
                  <div className="line-card expense-line" key={row.lineId ?? index}>
                    <label className="field field--grow"><span className="field__label">会社・ショップ名</span><input value={row.vendorName} maxLength={100} onChange={(event) => updateExpense(index, { ...row, vendorName: event.target.value })} /></label>
                    <MoneyInput label="金額" value={row.amount} min={1} onChange={(amount) => updateExpense(index, { ...row, amount })} />
                    <label className="field"><span className="field__label">支払方法</span><select value={row.paymentMethod} onChange={(event) => updateExpense(index, { ...row, paymentMethod: event.target.value as ExpenseLine["paymentMethod"] })}>{config.expensePaymentMethods.map((method) => <option key={method} value={method}>{methodLabels[method]}</option>)}</select></label>
                    <label className="field field--grow"><span className="field__label">用途・メモ</span><input value={row.memo ?? ""} maxLength={300} onChange={(event) => updateExpense(index, { ...row, memo: event.target.value })} /></label>
                    <RowActions onRemove={() => removeExpense(index)} onDuplicate={() => change((draft) => { draft.expenses.splice(index + 1, 0, { ...row, lineId: crypto.randomUUID() }); return draft; })} />
                  </div>
                ))}
              </div>
            )}
            <div className="section-total"><span>経費 合計</span><strong>{yen(calculation.expenseTotal)}</strong></div>
          </Section>

          <Section
            title="勤務明細"
            description="ノーゲス日でも出勤したスタッフの勤務時間を記録します。"
            icon={<UsersRound size={19} />}
            action={<AddButton onClick={() => change((draft) => ({ ...draft, shifts: [...draft.shifts, newShift(businessDate, config)] }))}>勤務を追加</AddButton>}
          >
            {payload.shifts.length === 0 ? <p className="empty-inline">勤務がある場合は追加してください。</p> : (
              <div className="line-list">
                {payload.shifts.map((row, index) => {
                  const staff = config.staff.find((item) => item.staffId === row.staffId);
                  const shiftCalculation = calculation.shifts[index];
                  return (
                    <div className="line-card shift-line" key={row.lineId ?? index}>
                      <label className="field"><span className="field__label">氏名</span><select value={row.staffId} onChange={(event) => { const nextStaff = config.staff.find((item) => item.staffId === event.target.value); updateShift(index, { ...row, staffId: event.target.value, staffName: nextStaff?.staffName, baseHourlyRateSnapshot: nextStaff?.hourlyRate }); }}>{config.staff.filter((item) => item.active || item.staffId === row.staffId).map((item) => <option value={item.staffId} key={item.staffId}>{item.staffName}</option>)}</select></label>
                      <label className="field"><span className="field__label">開始</span><input type="datetime-local" value={toLocalDateTime(row.startedAt)} onChange={(event) => updateShift(index, { ...row, startedAt: toOffsetIso(event.target.value) })} /></label>
                      <label className="field"><span className="field__label">終了</span><input type="datetime-local" value={toLocalDateTime(row.endedAt)} onChange={(event) => {
                        const localValue = event.target.value;
                        let endedAt = toOffsetIso(localValue);
                        if (endedAt && new Date(endedAt).getTime() <= new Date(row.startedAt).getTime()) {
                          endedAt = `${addDays(row.startedAt.slice(0, 10), 1)}T${localValue.slice(11)}:00+09:00`;
                        }
                        updateShift(index, { ...row, endedAt });
                      }} /></label>
                      <label className="field"><span className="field__label">休憩（分）</span><input type="number" min="0" step="1" value={row.breakMinutes} onChange={(event) => updateShift(index, { ...row, breakMinutes: Number(event.target.value) })} /></label>
                      <label className="field"><span className="field__label">当日支払</span><select value={row.paidTodayMethod} onChange={(event) => updateShift(index, { ...row, paidTodayMethod: event.target.value as ShiftLine["paidTodayMethod"] })}><option value="UNPAID">未払い</option><option value="CASH">現金</option><option value="OTHER">その他</option></select></label>
                      <div className="shift-result">
                        <span>時給 {yen(row.baseHourlyRateSnapshot ?? staff?.hourlyRate ?? 0)}</span>
                        {shiftCalculation ? <><span>通常 {minutesLabel(shiftCalculation.normalMinutes)}</span><span>深夜 {minutesLabel(shiftCalculation.lateNightMinutes)}</span><strong>{yen(shiftCalculation.totalWage)}</strong></> : <span className="is-negative">時刻を確認</span>}
                      </div>
                      <RowActions onRemove={() => removeShift(index)} />
                    </div>
                  );
                })}
              </div>
            )}
            <div className="section-total"><span>バイト代 合計</span><strong>{yen(calculation.wageTotal)}</strong></div>
          </Section>

          <Section title="ノーゲス日の集計・保存" description="売上は0円で記録します。レジ金は日次入力から任意で入力できます。" icon={<ClipboardCheck size={19} />} tone="accent">
            <dl className="result-list">
              <div><dt>当日売上</dt><dd>{yen(0)}</dd></div>
              <div><dt>当日経費</dt><dd>− {yen(calculation.expenseTotal)}</dd></div>
              <div><dt>当日バイト代</dt><dd>− {yen(calculation.wageTotal)}</dd></div>
              <div className="result-list__profit"><dt>当日利益</dt><dd className="is-negative">{yen(calculation.dailyProfit)}</dd></div>
            </dl>
            <ErrorSummary messages={validationMessages} />
            <div className="save-panel">
              <div>
                <strong>{meta ? `revision ${meta.revision + 1} として更新` : "ノーゲス日として新規保存"}</strong>
                <span>来客・決済・未収は0件で保存します。</span>
              </div>
              {saveState === "conflict" ? <button type="button" className="button button--quiet" onClick={() => loadDate(businessDate)}><RotateCcw size={17} /> 最新を再読込</button> : null}
              <button type="button" className="button button--primary button--save" disabled={saveState === "saving" || validationMessages.length > 0 || (saveState !== "dirty" && saveState !== "error" && saveState !== "conflict" && !!meta)} onClick={save}>
                {saveState === "saving" ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}
                {saveState === "saving" ? "保存しています…" : meta ? "ノーゲス日を更新" : "ノーゲス日を保存"}
              </button>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
